import { useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { useDismiss } from './useDismiss';

/**
 * Botao "Exportar" com os tres formatos — RF-REL-11.
 *
 * ================================================================
 * O MENU DIZ PARA QUE SERVE CADA FORMATO
 * ================================================================
 *
 * "PDF / XLSX / CSV" sozinho obriga a pessoa a saber a diferenca. A legenda
 * ("para imprimir", "para conferir", "para outro sistema") responde a pergunta
 * que ela realmente tem, que e qual escolher.
 *
 * ================================================================
 * O BOTAO FICA OCUPADO ENQUANTO GERA
 * ================================================================
 *
 * Um PDF de doze meses leva alguns segundos. Sem sinal nenhum, a reacao natural
 * e clicar de novo — e baixar o mesmo arquivo tres vezes.
 */

type Formato = 'pdf' | 'xlsx' | 'csv';

const FORMATOS: Array<{ id: Formato; label: string; hint: string }> = [
  { id: 'pdf', label: 'PDF', hint: 'para imprimir e arquivar' },
  { id: 'xlsx', label: 'Excel', hint: 'para conferir e somar' },
  { id: 'csv', label: 'CSV', hint: 'para levar a outro sistema' },
];

export function ExportButton({
  path,
  query,
  entityIds,
  disabled,
}: {
  /** Caminho da API sem `/api/v1`, ex.: `/reports/income-statement/export`. */
  path: string;
  /** Os MESMOS filtros que a tela está usando — sem eles o arquivo é outro recorte. */
  query: Record<string, string | number | undefined | null>;
  entityIds?: number[];
  disabled?: boolean;
}) {
  const [aberto, setAberto] = useState(false);
  const [gerando, setGerando] = useState<Formato | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const caixa = useDismiss<HTMLDivElement>(aberto || erro !== null, () => {
    setAberto(false);
    setErro(null);
  });

  async function exportar(formato: Formato) {
    setGerando(formato);
    setErro(null);

    try {
      const params = new URLSearchParams({ format: formato });

      for (const [chave, valor] of Object.entries(query)) {
        if (valor !== undefined && valor !== null && valor !== '') {
          params.set(chave, String(valor));
        }
      }

      await api.download(`${path}?${params.toString()}`, { entityIds });
      setAberto(false);
    } catch (e) {
      // A recusa por excesso de linhas chega aqui com o texto que diz o que
      // fazer ("reduza o período"). Engolir isso deixaria o botão só piscando.
      setErro(e instanceof ApiError ? e.message : 'Não foi possível gerar o arquivo.');
    } finally {
      setGerando(null);
    }
  }

  return (
    <div className="relative" ref={caixa}>
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        disabled={disabled || gerando !== null}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      >
        {gerando ? (
          <>
            <Spinner />
            Gerando {gerando.toUpperCase()}…
          </>
        ) : (
          <>
            <IconeDownload />
            Exportar
          </>
        )}
      </button>

      {aberto && !gerando && !erro && (
        <div className="absolute right-0 z-20 mt-1 w-60 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
          {FORMATOS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => void exportar(f.id)}
              className="flex w-full items-baseline gap-2 px-3 py-2 text-left transition hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                {f.label}
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400">{f.hint}</span>
            </button>
          ))}
        </div>
      )}

      {erro && (
        <div
          role="alert"
          className="absolute right-0 z-20 mt-1 w-72 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 shadow-lg dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
        >
          {erro}
        </div>
      )}
    </div>
  );
}

function IconeDownload() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"
      />
    </svg>
  );
}

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
    </svg>
  );
}
